import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { podeGravarCadastroAdmin } from '../../../utils/perfil';
import { Num, PillStatus } from '../../../components/gps';
import PainelLista, { Coluna } from '../../../components/gps/PainelLista';
import MenuAcoes, { ResumoExclusao } from '../../../components/gps/MenuAcoes';
import Modal, { BotaoSecundario } from '../../../components/gps/Modal';
import ModalUsuario from '../../../components/gps/ModalUsuario';
import FiltroRegional from '../../../components/gps/FiltroRegional';
import { linhasIniciais } from '../../../components/gps/paginacao';

/* ============================================================================
   Usuarios - visual novo (shadcn + Tailwind).

   Contrato conferido em remoteAPI/src/app/users/users.service.ts:45:
     GET /users/paginado?page&rows&valor&campo&direcao&ordenar&status&regional
     -> content: [{ ...UsersEntity, perfil: Role[perfil], status: StatusCore[status],
                    usuarioRegionais: [...] }]

   TRES COISAS QUE ESTA TELA MOSTRA E A ANTIGA ESCONDIA:

   1. PERFIL como etiqueta com peso visual proprio. O perfil e a informacao mais
      consequente da linha - e ele que decide quem edita quem - e estava como
      texto solto do mesmo tamanho do e-mail.

   2. PENDENTE. `pendente` e a data em que o convite foi enviado sem que a pessoa
      tenha entrado ainda. Usuario pendente ocupa vaga, aparece nas listas e nao
      trabalha; ver isso na propria linha evita o "mas eu criei esse usuario".

   3. REGIONAIS. `usuarioRegionais` ja vem na resposta e a tela antiga descartava.
      Sem regional o usuario nao ve nada, entao a coluna vazia e diagnostico.

   CPF: chega como NUMERO do banco (coluna numerica), o que come o zero a
   esquerda. A formatacao aqui repoe os 11 digitos antes de mascarar - mesmo
   problema que ja tinha sido corrigido no formulario de edicao.
   ============================================================================ */

type Usuario = {
  codigo?: number;
  nome?: string;
  email?: string;
  cpf?: number | string;
  perfil?: string | number;
  status?: number | string;
  pendente?: string | null;
  versaoDesenvolvedor?: boolean;
  usuarioRegionais?: Array<{ codigo?: number; nome?: string }>;
};

/* PENDENTES e um SEGMENTO, nao um status.
   `pendente` no banco e a data do convite (coluna PENDENTE), nao um valor da coluna
   STATUS - um pendente costuma estar ATIVO. Por isso o valor 9 e uma sentinela da
   TELA: ao escolher este segmento a consulta vai com `status=-1&pendente=true`, e
   nao com `status=9`, que o backend nao conhece. */
const STATUS = { TODOS: -1, ATIVO: 1, INATIVO: 0, PENDENTES: 9 } as const;

// Tom por perfil: quanto mais autoridade, mais forte. Cor aqui carrega
// significado (o que a pessoa pode fazer), nao decoracao.
const TOM_PERFIL: Record<string, string> = {
  MASTER: 'bg-danger-soft text-danger',
  ADMINISTRADOR: 'bg-info-soft text-info',
  GERENTE: 'bg-soft text-muted-foreground',
  USUARIO: 'bg-soft text-faint',
};

function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

function ehAtivo(v: unknown) {
  return String(v).toUpperCase() === 'ATIVO' || Number(v) === 1;
}

function formatarCpf(valor: unknown) {
  if (valor === null || valor === undefined || valor === '') return '—';
  const digitos = String(valor).replace(/\D/g, '').padStart(11, '0');
  if (digitos.length !== 11) return String(valor);
  return `${digitos.slice(0, 3)}.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-${digitos.slice(9)}`;
}

Usuarios.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Usuarios() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [menuAberto, setMenuAberto] = useState<number | null>(null);
  const [apagando, setApagando] = useState<Usuario | null>(null);
  const [removendo, setRemovendo] = useState(false);
  // `undefined` = fechado; '0' = criar; qualquer outro = editar aquele codigo.
  const [editando, setEditando] = useState<string | undefined>(undefined);
  const [linhas, setLinhas] = useState<Usuario[]>([]);
  const [total, setTotal] = useState(0);
  // Recarrega os cartoes (Total/Ativos) depois de apagar ou salvar - antes so na montagem.
  const [versaoIndices, setVersaoIndices] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [busca, setBusca] = useState('');
  /* ATIVOS por padrao, e nao "Todos" - pedido de 25/08/2026.

     O trabalho do dia a dia acontece sobre o que esta ativo; os inativos sao
     historico. Abrindo em "Todos", a primeira pagina vinha misturada e era preciso
     filtrar toda vez para chegar ao que interessa.

     Os CONTADORES dos tres segmentos continuam vindo completos (vem de endpoint
     proprio de indices), entao o total geral segue visivel e "Todos" e um clique. */
  const [status, setStatus] = useState<number>(STATUS.ATIVO);
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  const [regional, setRegional] = useState<number | null>(null);
  // Vira propriedade do QueryBuilder (`user.${ordenar}`).
  const [ordenarPor, setOrdenarPor] = useState('nome');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('asc');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [indices, setIndices] = useState({ total: 0, ativos: 0, inativos: 0, pendentes: 0 });

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      // Uma caixa para tres campos, decidida pelo formato do que foi digitado:
      // arroba = e-mail, so digito = CPF, resto = nome. O `campo` vira
      // propriedade do QueryBuilder, entao so pode sair destas tres constantes.
      const termo = busca.trim();
      const soDigitos = termo.replace(/\D/g, '');
      const campo = termo.includes('@') ? 'email' : soDigitos.length >= 6 && soDigitos === termo.replace(/[.\-\s]/g, '') ? 'cpf' : 'nome';
      const valor = campo === 'cpf' ? soDigitos : termo;

      const resposta = await axios.get<{ content: Usuario[]; totalElements: number }>(
        `/users/paginado?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(valor)}&campo=${campo}` +
          `&direcao=${direcao}&ordenar=${ordenarPor}` +
          `&status=${status === STATUS.PENDENTES ? STATUS.TODOS : status}` +
          `&pendente=${status === STATUS.PENDENTES}` +
          `&regional=${regional ?? 'null'}`
      );
      setLinhas(resposta.data?.content || []);
      setTotal(numero(resposta.data?.totalElements));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os usuários.');
      setLinhas([]);
      setTotal(0); // sem isto o rodape seguia "Mostrando 0 de N" com a paginacao ativa
    } finally {
      setCarregando(false);
    }
  }, [pagina, busca, status, linhasPorPagina, regional, ordenarPor, direcao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    axios
      .get<any>('/users/findByStatus')
      .then((r) => {
        const d = r.data || {};
        setIndices({
          total: numero(d.total),
          ativos: numero(d.ativo),
          inativos: numero(d.inativo),
          pendentes: numero(d.pendente),
        });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0, pendentes: 0 }));
  }, [versaoIndices]);

  const colunas: Array<Coluna<Usuario>> = useMemo(
    () => [
      {
        chave: 'nome',
        ordenarPor: 'nome',
        exportar: (u) => u.nome ?? '',
        /* NOME E E-MAIL EM COLUNAS SEPARADAS (10/09/2026, a pedido).

           Vinham empilhados numa coluna so, chamada "Usuario". Empilhado
           parece economizar espaco, mas custa as duas coisas que uma tabela
           existe para dar: nao da para ORDENAR por e-mail, e nao da para
           varrer uma coluna de e-mails com o olho, porque cada linha alterna
           entre nome e endereco. Em suporte o e-mail e justamente o campo que
           se compara ("e esse mesmo o endereco?"), entao merece coluna. */
        rotulo: 'Nome',
        largura: 'minmax(180px,1.8fr)',
        esqueleto: 'w-40',
        render: (u) => (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="block truncate text-[12.5px] font-semibold">{u.nome || ' - '}</span>
            {u.pendente && (
              <span
                className="flex-none rounded bg-warn-soft px-1.5 py-0.5 text-[10px] font-bold text-warn"
                title="Convite enviado, primeiro acesso ainda não feito"
              >
                PENDENTE
              </span>
            )}
          </span>
        ),
      },
      {
        chave: 'email',
        ordenarPor: 'email',
        exportar: (u) => u.email ?? '',
        rotulo: 'E-mail',
        largura: 'minmax(190px,1.9fr)',
        esqueleto: 'w-44',
        render: (u) => <span className="block truncate text-[12px] text-muted-foreground">{u.email || '—'}</span>,
      },
      {
        chave: 'cpf',
        ordenarPor: 'cpf',
        exportar: (u) => formatarCpf(u.cpf),
        rotulo: 'CPF',
        largura: '150px',
        esqueleto: 'w-28',
        render: (u) => <Num className="text-[12px] text-muted-foreground">{formatarCpf(u.cpf)}</Num>,
      },
      {
        chave: 'perfil',
        ordenarPor: 'perfil',
        exportar: (u) => String(u.perfil ?? ''),
        rotulo: 'Perfil',
        largura: '150px',
        esqueleto: 'w-24',
        render: (u) => {
          const rotulo = String(u.perfil ?? '').toUpperCase() || '—';
          return (
            <span
              className={`inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-bold tracking-wide ${
                TOM_PERFIL[rotulo] || 'bg-soft text-muted-foreground'
              }`}
            >
              {rotulo}
            </span>
          );
        },
      },
      {
        chave: 'regionais',
        exportar: (u) => (u.usuarioRegionais || []).map((r) => r.nome).join(' | '),
        rotulo: 'Regionais',
        largura: 'minmax(150px,1.4fr)',
        esqueleto: 'w-28',
        render: (u) => {
          const lista = u.usuarioRegionais || [];
          if (!lista.length) {
            return (
              <span className="text-[12px] font-semibold text-danger" title="Sem regional o usuário não enxerga nenhum dado">
                sem regional
              </span>
            );
          }
          const primeira = lista[0]?.nome || '—';
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="block truncate text-[12px] text-muted-foreground">{primeira}</span>
              {lista.length > 1 && (
                <Num className="flex-none rounded bg-soft px-1 text-[11px] text-faint">+{lista.length - 1}</Num>
              )}
            </span>
          );
        },
      },
      {
        chave: 'status',
        ordenarPor: 'status',
        exportar: (u) => (ehAtivo(u.status) ? 'ATIVO' : 'INATIVO'),
        rotulo: 'Status',
        largura: '110px',
        esqueleto: 'w-16',
        render: (u) => <PillStatus ativo={ehAtivo(u.status)} />,
      },
      {
        chave: 'acao',
        rotulo: 'Opções',
        largura: '80px',
        alinhar: 'dir',
        esqueleto: 'nenhum',
        render: (u) => (
          <MenuAcoes<Usuario>
            linha={u}
            aberto={menuAberto === u.codigo}
            onAlternar={() => setMenuAberto(menuAberto === u.codigo ? null : u.codigo ?? null)}
            onFechar={() => setMenuAberto(null)}
            ancorarADireita
            acoes={[
              {
                rotulo: 'Abrir / editar',
                ao: (x) => x.codigo && setEditando(String(x.codigo)),
              },
              {
                rotulo: 'Apagar',
                perigo: true,
                visivel: podeGravarCadastroAdmin(user),
                ao: (x) => setApagando(x),
              },
            ]}
          />
        ),
      },
    ],
    [menuAberto, user]
  );

  const apagar = async () => {
    if (!apagando?.codigo) return;
    setRemovendo(true);
    try {
      await axios.delete(`/users/${apagando.codigo}`);
      enqueueSnackbar('Usuário apagado.', { variant: 'success' });
      setApagando(null);
      setVersaoIndices((v) => v + 1);
      // Era o unico item de uma pagina que nao e a primeira: volta uma pagina.
      // Sem isto a tela ficava em "Nenhum ... encontrado" com registros antes.
      if (linhas.length === 1 && pagina > 0) setPagina((p) => p - 1);
      else await carregar();
    } catch (e: any) {
      // O backend valida hierarquia (quem pode apagar quem) e devolve o motivo.
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível apagar o usuário.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <Page title="Usuários">
      <PainelLista<Usuario>
        titulo="Usuários"
        descricao={
          <>
            <Num>{(indices.total || total).toLocaleString('pt-BR')}</Num> usuários. Quem aparece aqui
            depende do seu perfil e das suas regionais — o backend filtra a lista, não só a tela.
          </>
        }
        acao={
          <button
            type="button"
            onClick={() => setEditando('0')}
            className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            + Novo usuário
          </button>
        }
        indicadores={[
          { rotulo: 'Total', valor: indices.total || total, legenda: 'cadastrados', tom: 'info' },
          {
            rotulo: 'Ativos',
            valor: indices.ativos,
            legenda: 'com acesso',
            tom: 'ok',
            base: indices.total || total,
          },
          {
            rotulo: 'Inativos',
            valor: indices.inativos,
            legenda: 'sem acesso',
            tom: 'danger',
            base: indices.total || total,
          },
          /* PENDENTES - quem pediu acesso pela tela publica e ainda nao entrou.
             Faltava: o selo PENDENTE existia linha a linha, mas para saber quantos
             estao esperando era preciso paginar 505 usuarios com o olho. Este e o
             numero que gera trabalho (alguem tem que liberar), entao ele merece
             cartao e segmento proprios. Tom `warn`: e pendencia, nao erro. */
          {
            rotulo: 'Pendentes',
            valor: indices.pendentes,
            legenda: 'aguardando liberação',
            tom: 'warn',
            base: indices.total || total,
          },
        ]}
        busca={busca}
        aoBuscar={(v) => {
          setPagina(0);
          setBusca(v);
        }}
        placeholderBusca="Nome, e-mail ou CPF"
        segmentos={[
          { valor: STATUS.TODOS as number, rotulo: 'Todos', total: indices.total || total },
          { valor: STATUS.ATIVO as number, rotulo: 'Ativos', total: indices.ativos },
          { valor: STATUS.INATIVO as number, rotulo: 'Inativos', total: indices.inativos },
          { valor: STATUS.PENDENTES as number, rotulo: 'Pendentes', total: indices.pendentes },
        ]}
        segmentoAtual={status}
        aoTrocarSegmento={(v) => {
          setPagina(0);
          setStatus(v);
        }}
        filtrosExtras={<FiltroRegional valor={regional} onChange={(v) => { setPagina(0); setRegional(v); }} />}
        ordenarPor={ordenarPor}
        direcao={direcao}
        aoOrdenar={(campo) => {
          if (campo === ordenarPor) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
          else { setOrdenarPor(campo); setDirecao('asc'); }
          setPagina(0);
        }}
        linhasPorPagina={linhasPorPagina}
        aoTrocarLinhasPorPagina={(n) => { setPagina(0); setLinhasPorPagina(n); }}
        aoLimparFiltros={() => { setBusca(''); setStatus(STATUS.TODOS); setRegional(null); setOrdenarPor('nome'); setDirecao('asc'); setPagina(0); }}
        nomeArquivo="usuarios"
        colunas={colunas}
        linhas={linhas}
        chaveLinha={(u, i) => String(u.codigo ?? `sem-codigo-${i}`)}
        // Abre o modal desta tela, e nao a pagina antiga (ver o comentario em
        // apks-novo: era o mesmo descuido nas quatro listas).
        aoClicarLinha={(u) => u.codigo && setEditando(String(u.codigo))}
        carregando={carregando}
        erro={erro}
        aoRecarregar={carregar}
        vazio="Nenhum usuário encontrado com esse filtro."
        pagina={pagina}
        totalPaginas={Math.max(1, Math.ceil(total / linhasPorPagina))}
        total={total}
        aoTrocarPagina={setPagina}
        rotuloPlural="usuários"
      />

      <ModalUsuario
        aberto={editando !== undefined}
        codigo={editando}
        onFechar={() => setEditando(undefined)}
        onSalvo={() => { setVersaoIndices((v) => v + 1); carregar(); }}
      />

      <Modal
        aberto={Boolean(apagando)}
        titulo="Apagar usuário?"
        descricao="A pessoa perde o acesso ao sistema. O servidor valida a hierarquia de perfil."
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={apagar}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Apagando...' : 'Apagar usuário'}
            </button>
          </>
        }
      >
        {apagando && (
          <ResumoExclusao
            titulo={apagando.nome || '—'}
            detalhe={apagando.email}
            extra={
              <>
                Perfil: <strong className="text-ink">{String(apagando.perfil ?? '—').toUpperCase()}</strong>
              </>
            }
          />
        )}
      </Modal>
    </Page>
  );
}
